import { Button, DialogActions, DialogContent, DialogTitle, Stack, Typography } from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import Dialog from "@mui/material/Dialog";
import { Dayjs } from "dayjs";
import React from "react";

interface StartSecondApplicationPhaseDialogProps {
  open: boolean;
  onClose: () => void;
  startDate: Dayjs | null;
  endDate: Dayjs | null;
  changeStartDate: (date: Dayjs | null) => void;
  changeEndDate: (date: Dayjs | null) => void;
  handleSubmit: () => void;
  errorStartDateInFuture: boolean;
  errorStartBeforeEnd: boolean;
  errorEndDate: boolean;
}

/**
 * Dialog to start the second application phase
 * @param open Whether the dialog is open
 * @param onClose Function to close the dialog
 * @param startDate Start date of the second application phase
 * @param endDate End date of the second application phase
 * @param changeStartDate Function to change the start date
 * @param changeEndDate Function to change the end date
 * @param handleSubmit Function to submit the form
 * @param errorStartDateInFuture Whether there is an error with the start date being in the future
 * @param errorStartBeforeEnd Whether there is an error with the start date being before the end date
 * @param errorEndDate Whether there is an error with the end date
 * @returns The StartSecondApplicationPhaseDialog component
 */
const StartSecondApplicationPhaseDialog = ({
  open,
  onClose,
  startDate,
  endDate,
  changeStartDate,
  changeEndDate,
  handleSubmit,
  errorStartDateInFuture,
  errorStartBeforeEnd,
  errorEndDate,
}: StartSecondApplicationPhaseDialogProps) => {
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle fontWeight={"bold"}>Starte die zweite Bewerbungsphase</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Stack>
            <Typography fontWeight={"bold"}>Start der zweiten Bewerbungsphase:</Typography>
            <DateTimePicker
              value={startDate}
              onChange={changeStartDate}
              slotProps={{
                textField: {
                  variant: "outlined",
                  size: "small",
                  helperText: errorStartDateInFuture
                    ? "Start der zweiten Bewerbungsphase muss in der Zukunft liegen"
                    : errorStartBeforeEnd
                    ? "Start der zweiten Bewerbungsphase muss vor dem Ende liegen"
                    : "",
                  error: errorStartDateInFuture || errorStartBeforeEnd,
                },
              }}
            />
          </Stack>
          <Stack>
            <Typography fontWeight={"bold"}>Ende der zweiten Bewerbungsphase:</Typography>
            <DateTimePicker
              value={endDate}
              onChange={changeEndDate}
              slotProps={{
                textField: {
                  variant: "outlined",
                  size: "small",
                  helperText: errorEndDate ? "Ende der zweiten Bewerbungsphase muss vor dem Start liegen" : "",
                  error: errorEndDate,
                },
              }}
            />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose}>
          Abbrechen
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!startDate || errorStartDateInFuture || errorStartBeforeEnd || errorEndDate || !endDate}
        >
          Starte die zweite Bewerbungsphase
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StartSecondApplicationPhaseDialog;
